/**
 * AccessIQ DEMO session context — state + company type for seeded demos.
 * Persists in localStorage; used by View As bar and Eligibility Dashboard Run checks.
 */
import { reactive } from 'vue';

export const DEMO_ORG_SLUG = 'demo';

export const DEMO_DEFAULT_STATE = 'IN';
export const DEMO_DEFAULT_COMPANY_TYPE = 'home_health';

/** All US states + DC for the picker. Seeded narrative packs are a subset on the backend. */
export const DEMO_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'U.S. Virgin Islands' },
];

/** Eleven healthcare company types (financial-plan verticals). */
export const DEMO_COMPANY_TYPES = [
  { id: 'home_health', label: 'Home Health' },
  { id: 'home_care', label: 'Home Care (non-clinical)' },
  { id: 'hospice', label: 'Hospice' },
  { id: 'assisted_living', label: 'Assisted Living' },
  { id: 'community_day', label: 'Community Day Centers' },
  { id: 'community_support', label: 'Community Support Centers' },
  { id: 'behavioral_health', label: 'Behavioral Health' },
  { id: 'nemt', label: 'NEMT' },
  { id: 'medical_lodging', label: 'Medical lodging / family housing' },
  { id: 'residential_waiver', label: 'Residential + Waiver (HCBS)' },
  { id: 'snf', label: 'Skilled Nursing / Nursing Facility' },
];

const STORAGE_KEY = 'demo_context';

const VALID_STATES = new Set(DEMO_STATES.map((s) => s.code));
const VALID_COMPANIES = new Set(DEMO_COMPANY_TYPES.map((c) => c.id));

export const demoContextState = reactive({
  state: DEMO_DEFAULT_STATE,
  companyType: DEMO_DEFAULT_COMPANY_TYPE,
});

function normalizeState(code) {
  const upper = String(code || '').trim().toUpperCase();
  return VALID_STATES.has(upper) ? upper : DEMO_DEFAULT_STATE;
}

function normalizeCompanyType(id) {
  const key = String(id || '').trim().toLowerCase();
  return VALID_COMPANIES.has(key) ? key : DEMO_DEFAULT_COMPANY_TYPE;
}

export function setDemoContext({ state, companyType } = {}) {
  if (state !== undefined) {
    demoContextState.state = normalizeState(state);
  }
  if (companyType !== undefined) {
    demoContextState.companyType = normalizeCompanyType(companyType);
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      state: demoContextState.state,
      companyType: demoContextState.companyType,
    }),
  );
}

export function restoreDemoContext() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setDemoContext({
        state: DEMO_DEFAULT_STATE,
        companyType: DEMO_DEFAULT_COMPANY_TYPE,
      });
      return;
    }
    const parsed = JSON.parse(saved);
    setDemoContext({
      state: parsed.state,
      companyType: parsed.companyType,
    });
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    setDemoContext({
      state: DEMO_DEFAULT_STATE,
      companyType: DEMO_DEFAULT_COMPANY_TYPE,
    });
  }
}

export function demoStateLabel(code = demoContextState.state) {
  const row = DEMO_STATES.find((s) => s.code === code);
  return row ? row.name : code;
}

export function demoCompanyTypeLabel(id = demoContextState.companyType) {
  const row = DEMO_COMPANY_TYPES.find((c) => c.id === id);
  return row ? row.label : id;
}

export function isDemoOrg(org) {
  return org?.slug === DEMO_ORG_SLUG;
}

restoreDemoContext();
